/**
 * 确定性 RNG。全工程禁用 Math.random()：
 * 所有随机都必须来自 mulberry32 派生的独立流。
 */

export type Rng = () => number;

/** mulberry32 —— 32 位确定性 PRNG，返回 [0, 1)。 */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a 字符串哈希，用于把玩家个人熵混入 offers 流。 */
export function hashString(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface RngStream {
  /** 原始 [0,1) 抽样。 */
  next(): number;
  /** 均匀浮点，[min, max)。 */
  range(min: number, max: number): number;
  /** 均匀整数，[min, max] 闭区间。 */
  int(min: number, max: number): number;
  chance(p: number): boolean;
  pick<T>(items: readonly T[]): T;
  /** Fisher–Yates 洗牌（原地）。 */
  shuffle<T>(items: T[]): T[];
  /** 按权重抽取，weights 与 items 等长。 */
  weighted<T>(items: readonly T[], weights: readonly number[]): T;
}

export function makeStream(seed: number): RngStream {
  const rng = mulberry32(seed);

  function range(min: number, max: number): number {
    return min + (max - min) * rng();
  }

  function int(min: number, max: number): number {
    return Math.floor(rng() * (max - min + 1)) + min;
  }

  function chance(p: number): boolean {
    return rng() < p;
  }

  function pick<T>(items: readonly T[]): T {
    const i = Math.floor(rng() * items.length);
    return items[i] as T;
  }

  function shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = items[i] as T;
      items[i] = items[j] as T;
      items[j] = tmp;
    }
    return items;
  }

  function weighted<T>(items: readonly T[], weights: readonly number[]): T {
    let total = 0;
    for (const w of weights) total += w;
    let roll = rng() * total;
    for (let i = 0; i < items.length; i++) {
      roll -= weights[i] ?? 0;
      if (roll < 0) return items[i] as T;
    }
    return items[items.length - 1] as T;
  }

  return { next: rng, range, int, chance, pick, shuffle, weighted };
}

/** 五条独立流：全局同题走 world/drops/boss，三选一走 offers（混入玩家熵）。 */
export interface StreamBundle {
  world: RngStream;
  offers: RngStream;
  drops: RngStream;
  boss: RngStream;
  fx: RngStream;
}

const SALT = {
  offers: 0x27d4eb2f,
  drops: 0x9e3779b9,
  boss: 0x85ebca6b,
  fx: 0xc2b2ae35,
} as const;

/**
 * 双层种子：
 * - world / drops / boss / fx 只取决于当日全局种子 → 全网同题；
 * - offers = 全局种子 + 玩家个人熵 → 同种子下不同账号三选一序列不同。
 */
export function createStreams(worldSeed: number, playerEntropy: string): StreamBundle {
  const entropy = hashString(playerEntropy);
  return {
    world: makeStream(worldSeed >>> 0),
    offers: makeStream((worldSeed ^ SALT.offers ^ entropy) >>> 0),
    drops: makeStream((worldSeed ^ SALT.drops) >>> 0),
    boss: makeStream((worldSeed ^ SALT.boss) >>> 0),
    fx: makeStream((worldSeed ^ SALT.fx) >>> 0),
  };
}
